import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { referralService } from '../../services/db';
import { ROUTES } from '../../constants/routes';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import SEO from '../../components/common/SEO';
import transparentLogo from '../../assets/images/Transparent_logo.png';

const ReferralRedirect = () => {
  const { code: pathCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const referralCode = pathCode || searchParams.get('ref') || searchParams.get('code') || '';
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!referralCode) {
      navigate(ROUTES.PUBLIC.HOME);
      return;
    }

    // 1. Persist referral code in session & local storage
    try {
      sessionStorage.setItem('pending_referral_code', referralCode.toLowerCase().trim());
      localStorage.setItem('pending_referral_code', referralCode.toLowerCase().trim());
    } catch (_) {}

    // 2. Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
    let platform = 'web';
    if (/android/i.test(userAgent)) {
      platform = 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      platform = 'ios';
    }

    // 3. Log click event and fetch campaign info
    const processReferral = async () => {
      try {
        await referralService.logEvent({
          code: referralCode,
          type: 'click',
          platform,
        });

        const camp = await referralService.getCampaignByCode(referralCode);
        setCampaign(camp);
      } catch (err) {
        console.error('Error processing referral:', err);
      } finally {
        setLoading(false);
      }
    };

    processReferral();
  }, [referralCode, navigate]);

  const handleContinueToWeb = () => {
    setRedirecting(true);
    if (campaign?.targetDestination === 'volunteer') {
      navigate(ROUTES.PUBLIC.VOLUNTEER);
    } else if (campaign?.targetDestination === 'donate') {
      navigate(ROUTES.PUBLIC.DONATE);
    } else {
      navigate(ROUTES.PUBLIC.HOME);
    }
  };

  return (
    <div style={{
      minHeight: '85vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'var(--clr-background)'
    }}>
      <SEO
        title="Join Bible Contest — Download & Test Your Scripture Knowledge"
        description="You've been invited to join Bible Contest! Play timed Scripture quizzes, compete in leagues, and grow in God's Word."
        canonicalUrl={`/r/${referralCode}`}
      />
      <div className="card" style={{
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        padding: '36px 28px',
        border: '1px solid var(--clr-border)',
        borderRadius: '24px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <img
            src={transparentLogo}
            alt="Bible Contest"
            style={{ width: '72px', height: '72px', objectFit: 'contain' }}
          />
        </div>

        {loading ? (
          <div>
            <Spinner size="lg" />
            <p style={{ marginTop: '16px', color: 'var(--clr-text-2)', fontSize: '14px' }}>
              Connecting you to Bible Contest...
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span style={{
              display: 'inline-block',
              margin: '0 auto',
              padding: '4px 12px',
              borderRadius: '9999px',
              background: 'rgba(99, 102, 241, 0.1)',
              color: 'var(--clr-primary)',
              fontSize: '12.5px',
              fontWeight: 700
            }}>
              🎉 You've Been Invited!
            </span>

            <h1 style={{ fontSize: '22px', fontWeight: 800, margin: '4px 0 0', color: 'var(--clr-text-1)' }}>
              {campaign?.name || 'Welcome to Bible Contest'}
            </h1>

            <p style={{ margin: 0, fontSize: '14px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>
              Test your Scripture knowledge, participate in church & youth leagues, and build a lifelong habit in God's Holy Word.
            </p>

            <div style={{
              background: 'var(--clr-surface-container)',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Referral Tag:</span>
              <code style={{ fontSize: '13.5px', color: 'var(--clr-primary)', fontWeight: 700 }}>
                {referralCode.toUpperCase()}
              </code>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
              <Button
                variant="primary"
                size="lg"
                onClick={handleContinueToWeb}
                disabled={redirecting}
                style={{ width: '100%' }}
              >
                {redirecting ? 'Opening...' : '📖 Enter Bible Contest'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralRedirect;
